import { AlertTriangle, Heart } from "lucide-react";
import { useNavigate, useParams } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Price } from "@/components/ui/price";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { GUEST_ONLY_ROUTES, PROTECTED_ROUTES } from "@/constants/routes";
import { useFavoriteToggleHandler } from "@/features/engagement/hooks/useFavoriteToggleHandler";
import { useWishlist } from "@/features/engagement/hooks/useWishlist";
import { ImageGallery } from "@/features/marketplace/components/ImageGallery";
import { useCreateChat } from "@/features/messaging/hooks/useCreateChat";
import { SellerCard } from "@/features/marketplace/components/SellerCard";
import { SpecAccordion } from "@/features/marketplace/components/SpecAccordion";
import { useProduct } from "@/features/marketplace/hooks/useProduct";
import type { ProductCondition } from "@/features/marketplace/types/product";
import { useAppSelector } from "@/store/hooks";
import { ApiError } from "@/types/api";
import { cn } from "@/utils/cn";
import { formatRelativeTime } from "@/utils/formatRelativeTime";

const CONDITION_LABEL: Record<ProductCondition, string> = {
  new: "New",
  "like-new": "Like New",
  used: "Used",
  "for-parts": "For Parts",
};

/**
 * docs/ui/ui-architecture.md screen #6 — Product Details. Viewing is
 * public; docs/ui/routes.md: "contacting a seller requires auth." That
 * gate is enforced here at the button, not by a route guard — the page
 * itself sits on a Public route, only the "Message Seller" action needs
 * auth. Chat isn't built yet, so an authenticated click lands on `/chat`
 * (still a placeholder) rather than a specific conversation — becomes a
 * real deep link once the messaging feature exists.
 */
export function ProductDetailsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((state) => state.auth.status === "authenticated");
  const currentUserId = useAppSelector((state) => state.auth.user?.id);
  const productQuery = useProduct(slug ?? "");
  const wishlistQuery = useWishlist();
  const favoriteIds = new Set(wishlistQuery.data?.map((item) => item.id) ?? []);
  const handleFavoriteToggle = useFavoriteToggleHandler(favoriteIds);
  const createChatMutation = useCreateChat();

  function handleMessageSeller() {
    if (!isAuthenticated) {
      const redirect = encodeURIComponent(`/product/${slug}`);
      navigate(`${GUEST_ONLY_ROUTES.login}?redirect=${redirect}`);
      return;
    }
    if (!productQuery.data) return;
    createChatMutation.mutate(productQuery.data.id, {
      onSuccess: (conversation) => {
        navigate(PROTECTED_ROUTES.chatConversation.replace(":conversationId", conversation.id));
      },
      onError: (error) => {
        showToast.error(error instanceof ApiError ? error.message : "Couldn't start a conversation.");
      },
    });
  }

  if (productQuery.isPending) {
    return (
      <div className="mx-auto grid max-w-[1280px] grid-cols-1 gap-8 px-4 py-8 lg:grid-cols-2">
        <Skeleton className="aspect-square w-full rounded-lg" />
        <div className="space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (productQuery.isError) {
    const isNotFound = productQuery.error instanceof ApiError && productQuery.error.status === 404;
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-8">
        <EmptyState
          icon={AlertTriangle}
          heading={isNotFound ? "Listing not found" : "Couldn't load this listing"}
          description={
            isNotFound
              ? "This listing may have been removed, or the link is incorrect."
              : productQuery.error instanceof ApiError
                ? productQuery.error.message
                : "Something went wrong. Please try again."
          }
          action={
            isNotFound ? undefined : { label: "Retry", onClick: () => void productQuery.refetch() }
          }
        />
      </div>
    );
  }

  const product = productQuery.data;
  const isSold = product.status === "sold";
  const isOwnListing = currentUserId === product.seller.id;

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <ImageGallery images={product.images} alt={product.title} />

        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="neutral">{CONDITION_LABEL[product.condition]}</Badge>
              {isSold && <Badge variant="danger">Sold</Badge>}
            </div>
            <h1 className="text-h1 text-foreground">{product.title}</h1>
            <Price value={product.price} originalValue={product.originalPrice} size="h3" />
            {product.negotiable && (
              <p className="text-body-sm text-muted-foreground">Price is negotiable</p>
            )}
            <p className="text-body-sm text-muted-foreground">
              {product.location.city}, {product.location.state}
            </p>
            <p className="text-caption text-muted-foreground">
              Listed {formatRelativeTime(product.createdAt)}
            </p>
          </div>

          <div className="flex gap-2">
            {!isOwnListing && (
              <Button
                type="button"
                fullWidth
                onClick={handleMessageSeller}
                disabled={isSold}
                isLoading={createChatMutation.isPending}
                className="flex-1"
              >
                {isSold ? "No longer available" : "Message Seller"}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={
                favoriteIds.has(product.id) ? "Remove from wishlist" : "Add to wishlist"
              }
              onClick={() => handleFavoriteToggle(product.id)}
            >
              <Heart
                className={cn(
                  "h-4 w-4",
                  favoriteIds.has(product.id) && "fill-destructive text-destructive",
                )}
                aria-hidden="true"
              />
            </Button>
          </div>

          <div className="space-y-2">
            <h2 className="text-h4 text-foreground">Description</h2>
            <p className="whitespace-pre-line text-body text-foreground">{product.description}</p>
          </div>

          {product.specs && product.specs.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-h4 text-foreground">Specifications</h2>
              <SpecAccordion specs={product.specs} />
            </div>
          )}

          <SellerCard seller={product.seller} />
        </div>
      </div>
    </div>
  );
}
